import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { STATUS_PILL_CONFIG } from "./StatusPill";

/**
 * Phase 24 — "This period" summary strip.
 *
 * Loads GET /api/tenant/hours/summary?worker_id=&from=&to= and renders
 * a compact tile per non-zero status bucket. Never invents statuses;
 * uses only the canonical four.
 *
 * Props:
 *   - workerId?: string — omitted = "me"
 *   - from, to: ISO dates
 *   - hideRejectedWhenZero: bool (default true)
 *   - variant: "worker" | "admin" — trivial style hint
 */
const ORDER = ["draft", "submitted", "approved", "rejected"];

function fmt(h) {
  return h == null ? "0h" : `${h.toFixed(2)}h`;
}

export default function HoursSummaryStrip({
  workerId, from, to, hideRejectedWhenZero = true, variant = "worker",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (workerId) params.worker_id = workerId;
        if (from) params.from = from;
        if (to) params.to = to;
        const { data } = await api.get("/tenant/hours/summary", { params });
        setData(data);
      } catch (_e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [workerId, from, to]);

  if (loading) {
    return (
      <div
        className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground"
        data-testid="hours-summary-loading"
      >
        Loading hours summary…
      </div>
    );
  }
  if (!data) return null;

  const visible = ORDER.filter((k) => {
    const b = data.by_status?.[k] || {};
    if (k === "rejected" && hideRejectedWhenZero && (b.paid_hours || 0) === 0 && (b.entry_count || 0) === 0) {
      return false;
    }
    return true;
  });

  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      data-testid={`hours-summary-strip-${variant}`}
    >
      {visible.map((k) => {
        const b = data.by_status?.[k] || {};
        const cfg = STATUS_PILL_CONFIG[k];
        return (
          <div
            key={k}
            className="card-elevate rounded-lg border border-border bg-card p-3 shadow-sm"
            data-testid={`hours-summary-tile-${k}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${cfg.cls}`}
              >
                <cfg.Icon className="h-3 w-3" strokeWidth={2.5} />
                {cfg.label}
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">
                {b.entry_count || 0} entr{b.entry_count === 1 ? "y" : "ies"}
              </span>
            </div>
            <div className="mt-2 font-display text-2xl font-semibold text-primary">
              {fmt(b.paid_hours)}
            </div>
            {(b.ot_hours || 0) > 0 && (
              <div className="text-[10px] text-muted-foreground">
                incl. OT {fmt(b.ot_hours)}
                {(b.dt_hours || 0) > 0 && ` · DT ${fmt(b.dt_hours)}`}
              </div>
            )}
          </div>
        );
      })}
      <div
        className="card-elevate rounded-lg border border-primary/20 bg-primary/5 p-3 shadow-sm"
        data-testid="hours-summary-tile-total"
      >
        <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          Total this range
        </div>
        <div className="mt-2 font-display text-2xl font-semibold text-primary">
          {fmt(data.total?.paid_hours)}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {data.total?.entry_count || 0} entries
        </div>
      </div>
    </div>
  );
}
